// Gestão → Status das áreas: indicadores da semana, cor (mesma do Asana), pontos de atenção, comparativo
// semanal dos atendimentos e prévia do e-mail. Fonte única: /api/gestao/status-areas (suporte-app).
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Link,
  Paper, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
} from "@material-ui/core";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import MailOutlineIcon from "@material-ui/icons/MailOutline";
import CompareArrowsIcon from "@material-ui/icons/CompareArrows";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import suporteApi, { mensagemErro } from "../api";
import { usePermissao } from "../permissoes";

const CORES = { on_track: "#1f9d55", at_risk: "#d69e2e", off_track: "#c53030", on_hold: "#718096" };

// semana ISO (AAAA-Wss) de uma data
const semanaIso = (d) => {
  const t = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dia = t.getUTCDay() || 7;
  t.setUTCDate(t.getUTCDate() + 4 - dia);
  const ano = t.getUTCFullYear();
  const sem = Math.ceil(((t - Date.UTC(ano, 0, 1)) / 86400000 + 1) / 7);
  return `${ano}-W${String(sem).padStart(2, "0")}`;
};
const deslocar = (semana, n) => {
  const [a, s] = semana.split("-W").map(Number);
  const jan4 = new Date(a, 0, 4);
  const seg = new Date(jan4);
  seg.setDate(jan4.getDate() - ((jan4.getDay() || 7) - 1) + (s - 1) * 7 + n * 7);
  return semanaIso(seg);
};
const dataCurta = (iso) => (iso ? iso.slice(8, 10) + "/" + iso.slice(5, 7) : "");

const CartaoArea = ({ a, onComparativo }) => (
  <Paper variant="outlined" style={{ padding: 14, borderLeft: `6px solid ${CORES[a.cor]}`, display: "flex", flexDirection: "column", gap: 6 }}>
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
      <Typography variant="subtitle1"><b>{a.nome}</b></Typography>
      <Chip size="small" label={a.cor_nome} style={{ background: CORES[a.cor], color: "#fff", fontWeight: 600 }} />
    </div>
    {a.observacao && <Typography variant="caption" color="textSecondary">{a.observacao}</Typography>}
    {a.indicadores.map((i) => (
      <Typography key={i.nome} variant="body2" style={{ color: i.fora ? CORES.off_track : undefined }}>
        {i.fora === true ? "⚠ " : i.fora === false ? "✓ " : "• "}{i.texto}
      </Typography>
    ))}
    {a.atencao.length > 0 && (
      <div style={{ marginTop: 6 }}>
        <Typography variant="caption" style={{ fontWeight: 700 }}>Pontos de atenção</Typography>
        {a.atencao.map((x, n) => (
          <Typography key={n} variant="body2" style={{ fontSize: 13 }}>
            - {x.link ? <Link href={x.link.replace(/^https?:\/\/[^/]+/, "")}>{x.texto}</Link> : x.texto}
          </Typography>
        ))}
      </div>
    )}
    {["ALARME", "COFRES"].includes(a.area) && a.apurado && (
      <Button size="small" startIcon={<CompareArrowsIcon />} onClick={() => onComparativo(a.area)} style={{ alignSelf: "flex-start" }}>
        Comparativo com a semana anterior
      </Button>
    )}
  </Paper>
);

const DialogComparativo = ({ semana, area, onClose }) => {
  const [c, setC] = useState(null);
  useEffect(() => {
    if (!area) return;
    setC(null);
    suporteApi.get("/api/gestao/status-areas/comparativo", { params: { semana, area } })
      .then((r) => setC(r.data)).catch((e) => { toast.error(mensagemErro(e)); onClose(); });
  }, [semana, area, onClose]);
  const cor = (t) => (t === "melhor" ? CORES.on_track : t === "pior" ? CORES.off_track : undefined);
  const delta = (d, u = "") => (d == null ? "" : d === 0 ? "=" : `${d > 0 ? "+" : ""}${d}${u}`);
  return (
    <Dialog open={!!area} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Relatório semanal de atendimentos {c ? `— ${c.nome}` : ""}</DialogTitle>
      <DialogContent>
        {!c ? <CircularProgress size={24} /> : (
          <>
            <Typography variant="caption" color="textSecondary">
              Semana {dataCurta(c.periodo.de)} a {dataCurta(c.periodo.ate)} · anterior {dataCurta(c.periodo.anterior.de)} a {dataCurta(c.periodo.anterior.ate)}.
              {" "}É o mesmo relatório que vai como tarefa no Asana (com o Excel anexado).
            </Typography>
            <Table size="small">
              <TableHead><TableRow><TableCell>Indicador</TableCell><TableCell>Semana</TableCell><TableCell>Anterior</TableCell><TableCell>Variação</TableCell></TableRow></TableHead>
              <TableBody>
                {c.resumo.map((l) => (
                  <TableRow key={l.nome}>
                    <TableCell>{l.nome}</TableCell>
                    <TableCell><b>{l.atual ?? "não apurado"}{l.atual != null ? l.unidade : ""}</b></TableCell>
                    <TableCell>{l.anterior ?? "—"}{l.anterior != null ? l.unidade : ""}</TableCell>
                    <TableCell style={{ color: cor(l.tendencia) }}>{delta(l.delta, l.unidade)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {Object.entries(c.quebras).filter(([, itens]) => itens.length).map(([nome, itens]) => (
              <div key={nome} style={{ marginTop: 14 }}>
                <Typography variant="subtitle2">{nome}</Typography>
                <Table size="small">
                  <TableBody>
                    {itens.map((x) => (
                      <TableRow key={x.nome}><TableCell>{x.nome}</TableCell><TableCell><b>{x.atual}</b></TableCell>
                        <TableCell>{x.anterior}</TableCell><TableCell>{delta(x.delta)}</TableCell></TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ))}
          </>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Fechar</Button></DialogActions>
    </Dialog>
  );
};

const StatusAreas = () => {
  const [semana, setSemana] = useState(() => deslocar(semanaIso(new Date()), -1));
  const [d, setD] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [comparativo, setComparativo] = useState(null);
  const podePublicar = usePermissao("integracoes.gerenciar");
  const fecharComparativo = useCallback(() => setComparativo(null), []);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      setD((await suporteApi.get("/api/gestao/status-areas", { params: { semana } })).data);
    } catch (e) {
      toast.error(mensagemErro(e));
    }
    setCarregando(false);
  }, [semana]);
  useEffect(() => { carregar(); }, [carregar]);

  const previaEmail = async () => {
    try {
      const { data } = await suporteApi.get("/api/gestao/status-areas/email", { params: { semana }, responseType: "text" });
      const janela = window.open("", "_blank");
      if (janela) { janela.document.write(data); janela.document.close(); }
    } catch (e) {
      toast.error(mensagemErro(e));
    }
  };

  const publicar = async () => {
    if (!window.confirm(`Publicar o status da semana ${semana} (e-mail e Asana)? Respeita o modo de teste configurado em Integrações e não duplica o que já foi enviado.`)) return;
    try {
      const { data } = await suporteApi.post("/api/gestao/status-areas/publicar", { semana, canais: ["email", "asana"] });
      const resumo = ["email", "asana", "asana_relatorio"].map((k) => `${k}: ${(data[k] || []).map((x) => x.resultado).join(", ") || "—"}`).join(" | ");
      toast.info(resumo, { autoClose: 12000 });
    } catch (e) {
      toast.error(mensagemErro(e));
    }
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>Status das áreas</Title>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <IconButton size="small" onClick={() => setSemana(deslocar(semana, -1))}><ChevronLeftIcon /></IconButton>
          <TextField id="status-semana" size="small" variant="outlined" value={semana} label="Semana"
            onChange={(e) => setSemana(e.target.value)} style={{ width: 120 }} />
          <IconButton size="small" onClick={() => setSemana(deslocar(semana, 1))}><ChevronRightIcon /></IconButton>
          <Tooltip title="Abre o e-mail semanal como ele será enviado">
            <Button size="small" variant="outlined" startIcon={<MailOutlineIcon />} onClick={previaEmail}>Prévia do e-mail</Button>
          </Tooltip>
          {podePublicar && <Button size="small" variant="contained" color="primary" onClick={publicar}>Publicar agora</Button>}
        </div>
      </MainHeader>
      {carregando && !d ? <CircularProgress size={28} /> : d && (
        <>
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 10 }}>
            {dataCurta(d.de)} a {dataCurta(d.ate)} · mesma cor e mesmos números do status no Asana e do e-mail semanal.
            Indicador sem dado aparece como “não apurado” e não muda a cor.
          </Typography>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 12, paddingBottom: 20 }}>
            {d.areas.map((a) => <CartaoArea key={a.area} a={a} onComparativo={setComparativo} />)}
          </div>
        </>
      )}
      <DialogComparativo semana={semana} area={comparativo} onClose={fecharComparativo} />
    </MainContainer>
  );
};

export default StatusAreas;
